import React, { useCallback, useContext, useEffect, useMemo, useState } from "react";
import ReactTable from "components/framework/table/ReactTable";
import { IntlShape, useIntl } from "react-intl";
import { CarrierIntegrationApi } from "services/apis/CarrierIntegrationApi";
import { CarrierIntegrationListDto } from "services/apis/types/carrierIntegration/CarrierIntegrationDto";
import { useIsMounted } from "services/customHooks/useIsMounted";
import { handleError } from "services/util/ApiUtil";
import { LoadingIndicator } from "components/framework/loadingIndicator/LoadingIndicator";
import { CarrierTableColumnIdentifiers } from "./types/CarrierTableColumnIdentifiers";
import CarrierActionsCell from "./CarrierActionsCell";
import AddCarrierIntegrationModal from "./AddCarrierIntegrationModal";
import SpIdCell from "components/common/SpIdCell";
import { AppContext } from "services/appContext/AppContext";

type Props = {
  refreshTrigger?: boolean;
  searchValue?: string;
};
export default function CarrierIntegrationTable(props: Props) {
  const [showLoadingIndicator, setShowLoadingIndicator] = useState(false);
  const [carrierItems, setCarrierItems] = useState<CarrierIntegrationListDto[]>();
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [selectedRowData, setSelectedRowData] = useState<any>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const { appContext } = useContext(AppContext);
  const theme = appContext.theme;
  const handleEditClick = useCallback((rowData: any) => {
    setSelectedRowData(rowData);
    setIsEditMode(true);
    setIsModalOpen(true);
  }, []);
  const intl = useIntl();
  const isMounted = useIsMounted();
    const toggleRefresh = useCallback(() => {
    setShowLoadingIndicator(true);
    CarrierIntegrationApi.list(props.searchValue)
      .then((result) => {
        if (isMounted.current) {
          setCarrierItems(result);
        }
      })
      .catch((error) => handleError(error))
      .finally(() => {
        if (isMounted.current) {
          setShowLoadingIndicator(false);
        }
      });
  }, [isMounted, props.searchValue]);

  const columns = useMemo(() => getColumns(intl, handleEditClick, toggleRefresh, theme), [intl, handleEditClick, toggleRefresh]);

  useEffect(() => {
    toggleRefresh();
  }, [toggleRefresh, props.refreshTrigger]);

  const handleAddNew = () => {
    setIsEditMode(false);
    setSelectedRowData(null);
    setIsModalOpen(true);
  };

  return (
    <>
      {showLoadingIndicator ? (
        <LoadingIndicator white />
      ) : (
        <>
          <ReactTable
            showViewSettings
            title={intl.formatMessage({ id: "carrierIntegration.table.title" })}
            columns={columns}
            data={carrierItems}
            onRefresh={toggleRefresh}
            hideSearch={true}
          />
          {isEditMode && (
            <AddCarrierIntegrationModal
              isEditMode={true}
              initialData={selectedRowData}
              onClose={() => setIsEditMode(false)}
              onSuccess={() => {
                setIsEditMode(false);
                toggleRefresh();
              }}
            />
          )}
        </>
      )}
    </>
  );
}

const getColumns = (intl: IntlShape, handleEditClick, toggleRefresh,  theme: string) => [
  {
    Header: intl.formatMessage({ id: "customerInventory.table.customerName" }),
    accessor: CarrierTableColumnIdentifiers.CustomerName
  },
  {
    Header: intl.formatMessage({ id: "spids.table.spId" }),
    accessor: CarrierTableColumnIdentifiers.SPID,
    Cell: (cell: any) => cell?.cell?.row?.original?.spids?.split(", ")?.map(el => <SpIdCell spId={el} isTpp={true} labelColor={`${theme === "light" ? "spid-bg-lgreen" : ""}`} identifier={"lgreen"} />)
  },
  {
    Header: intl.formatMessage({ id: "carrierIntegration.losingcarrierspid" }),
    accessor: CarrierTableColumnIdentifiers.LosingCarrierSpid,
    Cell: (cell: any) => <SpIdCell spId={cell.cell.row.original.losingCarrierSpid} labelColor={`${theme === "light" ? "bg-lblue" : ""}`} />

  },
  {
    Header: intl.formatMessage({ id: "orders.order.table.actions" }),
    accessor: CarrierTableColumnIdentifiers.Actions,
    Cell: (cell: any) => <CarrierActionsCell row={cell.row} onEdit={handleEditClick} onRefresh={toggleRefresh} />
  }
];
